'use client';

import { useState, Dispatch, SetStateAction } from 'react';
import OTPVerification from './OTPVerification';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '@/services/authService';
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { useSnackbar } from '@/context/SnackbarContext';

interface LoginFormProps {
  onOTPScreenChange?: Dispatch<SetStateAction<boolean>>;
}

export default function LoginForm({ onOTPScreenChange }: LoginFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  // const [sessionUser, setSessionUser] = useState(null);
  const [showOTP, setShowOTP] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setSession, setSessionUser, fetchSession,sessionLoading } = useAuth();
  const { showSnackbar } = useSnackbar();
  // const isMobileNumber = (value: string) => {
  //   // Check if the value contains any numbers
  //   return /^\d+$/.test(value);
  // };

  // const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
      try {
        if (identifier) {
          const response = await authService.login({ email: identifier });          
          if ((response as any)?.success && (response as any)?.data?.messageId) {            
            // toast.success("otp sent your email address")
            showSnackbar({
              message: "otp sent your email address",
              type: 'success',
            });
            setShowOTP(true);
            localStorage.setItem("peoples_token", "");
            onOTPScreenChange?.(true);
          }
        } else {
          // Handle email login differently if needed
          console.log('Email login attempt with:', identifier);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          // toast.error(error.response?.data?.message)
          showSnackbar({
            message: error.response?.data?.message,
            type: 'error',
          });
        } else {
          console.log('error', error);
        }
        setIsSubmitting(false);
        
      }
      finally {
        setIsSubmitting(false);
      }
      
  };

  const handleOTPVerify = async (otp: string) => {
    try {
      const response = await authService.verifyOtp({ email: identifier, otp: otp });
      // console.log('response verifyOtp', response);
      if ((response as any)?.success) {
        // await setSessionUser({ ...(response as any)?.data?.newUser, token: (response as any)?.data?.token, verified: (response as any)?.data?.verified });
        const userData = {
          ...(response as any)?.data?.newUser,
          token: (response as any)?.data?.token,
          verified: (response as any)?.data?.verified,
        };
        // console.log('userData', userData);
        localStorage.setItem("peoples_token", (response as any)?.data?.token);
        setSessionUser(userData)
        await setSession(userData);
        // toast.success("You're now signed in.")   
        showSnackbar({
          message: "You're now signed in.",
          type: 'success',
        });
        await fetchSession()   
        
        // Get saved return URL and redirect
        console.log(`🔄 [LOGIN_FORM] Starting post-authentication redirect`);
        const returnUrl = searchParams?.get('redirect') || '/';
        console.log(`🎯 [LOGIN_FORM] Redirecting user to: ${returnUrl}`);
        router.push(returnUrl);
      
      } else {
        // toast.error("Invalid OTP. Please try again.");
        showSnackbar({
          message: "Invalid OTP. Please try again.",
          type: 'error',
        });
      }
      
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // toast.error(error.response?.data?.message)
        showSnackbar({
          message: error.response?.data?.message,
          type: 'error',
        });
      } else {
        console.log('error', error);
      }
    }

    // Handle OTP verification here
   
 
  };

  const handleChangeNumber = () => {
    setShowOTP(false);

    onOTPScreenChange?.(false);
  };


  // useEffect(() => {
  //    if (typeof window !== "undefined" && Number(sessionUser?.subscriber_id) > 0) {
  //     console.log("Redirecting to home...");
  //     // Check for stored return URL and redirect there, otherwise go to home
  //     const returnUrl = getReturnUrlWithFallback();
  //     if (returnUrl) {
  //       router.push(returnUrl);
  //       clearPreservedReturnUrl();
  //     } else {
  //       router.push('/'); // Don't use window.location.href
  //     }
  //   }
  // }, [sessionUser, router]);

  const inputVariants = {
    focus: { scale: 1.02, transition: { duration: 0.2 } },
    blur: { scale: 1, transition: { duration: 0.2 } }
  };

  const labelVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } }
  };

  const buttonVariants = {
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
    disabled: { opacity: 0.7 }
  };

  if (showOTP) {
    return (
      <OTPVerification
        phoneNumber={identifier}
        emailId={identifier}
        onVerify={handleOTPVerify}
        onChangeNumber={handleChangeNumber}
        onResendOTP={async () => {
        const response = await authService.login({ email: identifier });
        if ((response as any)?.success) {
          // toast.success("New OTP sent to your email address");
          showSnackbar({
            message: "New OTP sent to your email address",
            type: 'success',
          });
        }
      }}
      />
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div>
        <div className="relative">
          <motion.input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            // onChange={(e) => {
            //   const value = e.target.value;
            //   // If only digits, restrict to 10
            //   if (/^\d*$/.test(value)) {
            //     setIdentifier(value.slice(0, 10));
            //   } else {
            //     setIdentifier(value);
            //   }
            // }}
            placeholder="Enter Email"
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
            required
            whileFocus="focus"
            variants={inputVariants}
            initial="blur"
          // maxLength={10}
          // inputMode="numeric"
          />
          <AnimatePresence>
            {identifier && (
              <motion.span
                className="absolute capitalize text-xs text-black -top-2 left-2 bg-white px-1"
                variants={labelVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {/* {isMobileNumber(identifier) ? 'Mobile Number' : 'Email'} */}
                Email
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      <motion.button
        type="submit"
        className={`w-full py-3 rounded-md transition-colors ${identifier && !sessionLoading
          ? 'bg-orange text-white font-medium hover:bg-orange'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        whileHover={identifier ? "hover" : "disabled"}
        whileTap={identifier ? "tap" : "disabled"}
        variants={buttonVariants}
        disabled={isSubmitting || sessionLoading}
      >
        LOGIN
      </motion.button>
    </motion.form>
  );
}